import React from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_TYPE_OPTIONS } from '../dto/system-trading-servers.dto.js';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';

function SystemTradingServersPageView({
  rows,
  platformOptions,
  isLoading,
  isSaving,
  error,
  success,
  detailsRow,
  hasPlatforms,
  platformLabelByValue,
  isCreateOpen,
  editingTargetId,
  deleteTargetId,
  form,
  formErrors,
  modalError,
  closeModals,
  openCreate,
  openEdit,
  openDetails,
  requestDelete,
  onFormChange,
  toggleAccountType,
  saveForm,
  confirmDelete,
}) {
  const { t } = useTranslation();
  const capabilities = platformOptions.find(option => option.value === form.tradingPlatform)?.capabilities ?? form.capabilities ?? {};
  const managerCredential = capabilities.credentialType === 'manager_token';
  const accountTypes = capabilities.allowedAccountTypes ?? ACCOUNT_TYPE_OPTIONS;
  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('tradingServers.savingInfo') : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('tradingServers.title')}</h2>
          <p>{t('tradingServers.subtitle')}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={openCreate}
          disabled={isLoading || isSaving || !hasPlatforms}
        >
          {t('tradingServers.addTradingServer')}
        </button>
      </div>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th style={{ width: '130px' }}>{t('tradingServers.colPlatform')}</th>
                    <th>{t('tradingServers.colName')}</th>
                    <th>{t('tradingServers.colDescription')}</th>
                    <th>{t('tradingServers.colDsn')}</th>
                    <th>{t('tradingServers.colProxy')}</th>
                    <th style={{ width: '180px' }}>{t('tradingServers.colAccountTypes')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('tradingServers.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">{t('tradingServers.noTradingServersFound')}</td>
                    </tr>
                  ) : null}

                  {rows.map((row) => (
                    <tr key={row.id} className="app-clickable-row" onClick={() => openDetails(row.id)}>
                      <td>{platformLabelByValue.get(String(row.tradingPlatform || '').trim()) || row.tradingPlatform?.toUpperCase() || '-'}</td>
                      <td>{row.name || '-'}</td>
                      <td>{row.description || '-'}</td>
                      <td>{row.dsn ? '<hidden>' : '-'}</td>
                      <td className="text-break">{row.proxy || '-'}</td>
                      <td>{row.accountTypes.join(', ') || '-'}</td>
                      <td className="text-end text-nowrap">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(row);
                            }}
                            disabled={isSaving}
                            title="Edit"
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={(event) => {
                              event.stopPropagation();
                              requestDelete(row.id);
                            }}
                            disabled={isSaving}
                            title="Delete"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </LoadingContainer>
        </div>
      </div>

      {detailsRow ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('tradingServers.detailsTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldPlatform')}:</strong>
                    <div>{platformLabelByValue.get(String(detailsRow.tradingPlatform || '').trim()) || detailsRow.tradingPlatform?.toUpperCase() || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldName')}:</strong>
                    <div>{detailsRow.name || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldDescription')}:</strong>
                    <div>{detailsRow.description || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldAccountTypes')}:</strong>
                    <div>{detailsRow.accountTypes.join(', ') || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldDsn')}:</strong>
                    <div className="font-monospace text-break">{detailsRow.dsn || '-'}</div>
                  </div>
                  {platformOptions.find(option => option.value === detailsRow.tradingPlatform)?.capabilities?.credentialType === 'manager_token' ? <p>Tenant manager token: {detailsRow.hasManagerToken ? 'Configured' : 'Not configured'}</p> : (<>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldDealerLogin')}:</strong>
                    <div className="font-monospace text-break">{detailsRow.dealerLogin || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldDealerPassword')}:</strong>
                    <div className="font-monospace text-break">{detailsRow.dealerPassword || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>{t('tradingServers.fieldDealerDsn')}:</strong>
                    <div className="font-monospace text-break">{detailsRow.dealerDsn || '-'}</div>
                  </div>
                  </>)}
                  <div>
                    <strong>{t('tradingServers.fieldProxy')}:</strong>
                    <div className="font-monospace text-break">{detailsRow.proxy || '-'}</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.close')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {isCreateOpen || editingTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingTargetId ? t('tradingServers.editTitle') : t('tradingServers.addTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <form onSubmit={saveForm}>
                  <div className="modal-body">
                    {modalError ? <div className="alert alert-danger py-2">{modalError}</div> : null}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label">{t('tradingServers.tradingPlatformLabel')}</label>
                        <select
                          className={`form-select ${formErrors.tradingPlatform ? 'is-invalid' : ''}`}
                          value={form.tradingPlatform}
                          onChange={(event) => onFormChange('tradingPlatform', event.target.value)}
                        >
                          <option value="">{t('tradingServers.selectPlatform')}</option>
                          {platformOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {formErrors.tradingPlatform ? <div className="invalid-feedback">{formErrors.tradingPlatform}</div> : null}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">{t('tradingServers.nameLabel')}</label>
                        <input
                          className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                          value={form.name}
                          onChange={(event) => onFormChange('name', event.target.value)}
                          required
                        />
                        {formErrors.name ? <div className="invalid-feedback">{formErrors.name}</div> : null}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('tradingServers.descriptionLabel')}</label>
                      <input
                        className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                        value={form.description}
                        onChange={(event) => onFormChange('description', event.target.value)}
                        required
                      />
                      {formErrors.description ? <div className="invalid-feedback">{formErrors.description}</div> : null}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('tradingServers.dsnLabel')}</label>
                      <input
                        className={`form-control ${formErrors.dsn ? 'is-invalid' : ''}`}
                        value={form.dsn}
                        onChange={(event) => onFormChange('dsn', event.target.value)}
                        required
                      />
                      {managerCredential ? <div className="form-text">Enter the API connection URL provided by the trading platform.</div> : null}
                      {formErrors.dsn ? <div className="invalid-feedback">{formErrors.dsn}</div> : null}
                    </div>
                    {managerCredential ? (
                      <div className="mb-3">
                        <label className="form-label" htmlFor="platform-manager-token">Tenant manager token</label>
                        <input id="platform-manager-token" type="password" autoComplete="new-password" maxLength={128}
                          className={`form-control ${formErrors.managerToken ? 'is-invalid' : ''}`}
                          value={form.managerToken || ''} onChange={(event) => onFormChange('managerToken', event.target.value)} />
                        <div className="form-text">{form.hasManagerToken ? 'Token configured. Leave blank to keep it, or enter a replacement issued by the trading platform.' : 'Enter the manager token issued for your tenant in the trading platform.'}</div>
                        {formErrors.managerToken ? <div className="invalid-feedback">{formErrors.managerToken}</div> : null}
                      </div>
                    ) : (<>
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label">{t('tradingServers.dealerLoginLabel')}</label>
                        <input
                          className={`form-control ${formErrors.dealerLogin ? 'is-invalid' : ''}`}
                          value={form.dealerLogin}
                          onChange={(event) => onFormChange('dealerLogin', event.target.value)}
                        />
                        {formErrors.dealerLogin ? <div className="invalid-feedback">{formErrors.dealerLogin}</div> : null}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">{t('tradingServers.dealerPasswordLabel')}</label>
                        <input
                          className={`form-control ${formErrors.dealerPassword ? 'is-invalid' : ''}`}
                          value={form.dealerPassword}
                          onChange={(event) => onFormChange('dealerPassword', event.target.value)}
                        />
                        {formErrors.dealerPassword ? <div className="invalid-feedback">{formErrors.dealerPassword}</div> : null}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('tradingServers.dealerDsnLabel')}</label>
                      <input
                        className={`form-control ${formErrors.dealerDsn ? 'is-invalid' : ''}`}
                        value={form.dealerDsn}
                        onChange={(event) => onFormChange('dealerDsn', event.target.value)}
                      />
                      {formErrors.dealerDsn ? <div className="invalid-feedback">{formErrors.dealerDsn}</div> : null}
                    </div>
                    </>)}
                    <div className="mt-3">
                      <label className="form-label">{t('tradingServers.proxyLabel')}</label>
                      <input
                        className={`form-control ${formErrors.proxy ? 'is-invalid' : ''}`}
                        value={form.proxy}
                        onChange={(event) => onFormChange('proxy', event.target.value)}
                      />
                      {formErrors.proxy ? <div className="invalid-feedback">{formErrors.proxy}</div> : null}
                    </div>
                    <div className="mt-3">
                      <label className="form-label">{t('tradingServers.accountTypesLabel')}</label>
                      <div className={`border rounded p-3 ${formErrors.accountTypes ? 'border-danger' : ''}`}>
                        <div className="d-flex flex-wrap align-items-center gap-3">
                          {accountTypes.map((item) => {
                            const isChecked = Array.isArray(form.accountTypes) && form.accountTypes.includes(item);
                            return (
                              <div className="form-check m-0" key={item}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`trading-server-account-type-${item}`}
                                  checked={isChecked}
                                  disabled={accountTypes.length === 1}
                                  onChange={(event) => toggleAccountType(item, event.target.checked)}
                                />
                                <label className="form-check-label text-capitalize" htmlFor={`trading-server-account-type-${item}`}>
                                  {getEnumOptionLabel(t, 'accountType', item)}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {formErrors.accountTypes ? <div className="invalid-feedback d-block">{formErrors.accountTypes}</div> : null}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                      {editingTargetId ? t('common.save') : t('common.create')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {deleteTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('tradingServers.deleteTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  {t('tradingServers.deleteConfirm')}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isSaving}>{t('common.delete')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}
    </div>
  );
}

export default SystemTradingServersPageView;
